// Settings screen.
//
// Scope (Phase 1):
//   - Parent first name (entered at runtime per §9, never hardcoded in repo)
//   - Auto-lock timeout
//   - Lock-now button
//   - Delete all data (double-confirmed)
//
// Out of scope for v1:
//   - Change PIN — requires re-encrypting the whole DB with a new key.
//     The "Forgot PIN" wipe path covers the rare reset case for now.

import {
  getParentName, setParentName,
  getAutoLockMs, setAutoLockMs,
  wipeAllData, lock,
  DEFAULT_AUTO_LOCK_MS,
} from '../auth/session.js';
import { toast, esc } from './util.js';

const AUTO_LOCK_OPTIONS = [
  { ms: 30_000,    label: '30 seconds' },
  { ms: 60_000,    label: '1 minute' },
  { ms: 300_000,   label: '5 minutes' },
  { ms: 900_000,   label: '15 minutes' },
  { ms: -1,        label: 'Never (not recommended)' },
];

export function renderSettings(app) {
  const opts = AUTO_LOCK_OPTIONS
    .map((o) => `<option value="${o.ms}">${esc(o.label)}</option>`)
    .join('');

  app.innerHTML = `
    <header>
      <h1>Settings</h1>
      <p class="subtitle">Per-device. Nothing here leaves the phone until you sync.</p>
    </header>
    <main>
      <section class="card">
        <h2>Your first name</h2>
        <p class="muted small">Used to tag journal entries. First name only; never committed to code.</p>
        <div class="ctrl-row stack-mobile">
          <input id="parent-name" type="text" maxlength="24" placeholder="(optional)" />
          <button id="parent-name-save" class="btn btn--secondary">Save</button>
        </div>
      </section>

      <section class="card">
        <h2>Auto-lock</h2>
        <p class="muted small">Lock the app after this long in the background.</p>
        <div class="ctrl-row stack-mobile">
          <select id="auto-lock">${opts}</select>
          <button id="lock-now" class="btn btn--secondary">Lock now</button>
        </div>
      </section>

      <section class="card danger-card">
        <h2>Delete all data</h2>
        <p class="muted small">Erases every photo and journal entry on this device. Cannot be undone.</p>
        <button id="wipe" class="btn btn--danger">Delete everything</button>
      </section>
    </main>
    <footer>
      <a href="#/" class="link-btn">← Back to home</a>
    </footer>
  `;

  const nameInput = document.getElementById('parent-name');
  const autoLockSel = document.getElementById('auto-lock');

  (async () => {
    const name = await getParentName();
    if (name) nameInput.value = name;
    const ms = (await getAutoLockMs()) ?? DEFAULT_AUTO_LOCK_MS;
    autoLockSel.value = String(ms);
    if (!AUTO_LOCK_OPTIONS.some((o) => o.ms === Number(autoLockSel.value))) {
      // Stored value isn't in the option list; default to recommended.
      autoLockSel.value = String(DEFAULT_AUTO_LOCK_MS);
    }
  })();

  document.getElementById('parent-name-save').addEventListener('click', async () => {
    const saved = await setParentName(nameInput.value);
    toast(saved ? `Saved as "${saved}"` : 'Cleared.');
  });

  autoLockSel.addEventListener('change', async (e) => {
    const ms = Number(e.target.value);
    await setAutoLockMs(ms);
    toast('Auto-lock updated');
  });

  document.getElementById('lock-now').addEventListener('click', () => {
    lock();
  });

  document.getElementById('wipe').addEventListener('click', async () => {
    if (!confirm('Delete ALL photos and journal entries on this device?\n\nThis cannot be undone.')) return;
    if (!confirm('Final check — really delete everything?')) return;
    try {
      await wipeAllData();
      toast('All data deleted.');
    } catch (err) {
      console.error(err);
      toast(err.message, { kind: 'error' });
    }
  });

  return null;
}
